import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Upload, FileText, Music, BookOpen, Sparkles, Check, X,
  Loader2, ChevronRight, ChevronDown, Clock, AlertCircle,
  Brain, Volume2, Layers, RefreshCw, Eye, Trash2, Download, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('session_token');

const ContentGenerationFlow = ({ professions }) => {
  const [materials, setMaterials] = useState([]);
  const [topics, setTopics] = useState([]);
  const [pendingContent, setPendingContent] = useState([]);
  const [generationJobs, setGenerationJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [generationMode, setGenerationMode] = useState('material'); // 'material' or 'topics'
  const [selectedProfession, setSelectedProfession] = useState('');
  const [previewContent, setPreviewContent] = useState(null); // For preview modal
  const [generationOptions, setGenerationOptions] = useState({
    generate_questions: true,
    num_questions: 20,
    generate_flashcards: true,
    num_flashcards: 15,
    generate_audio: false,
    audio_type: 'summary',
    num_audios: 1,
    audio_duration_minutes: 3
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedProfession) {
      loadTopicsForProfession(selectedProfession);
    }
  }, [selectedProfession]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [materialsRes, pendingRes, jobsRes] = await Promise.all([
        axios.get(`${API}/drive/materials`, { headers }).catch(() => ({ data: { materials: [] } })),
        axios.get(`${API}/content/pending`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/content/jobs`, { headers }).catch(() => ({ data: { jobs: [] } }))
      ]);
      
      setMaterials(materialsRes.data.materials || []);
      setPendingContent(pendingRes.data.items || []);
      setGenerationJobs(jobsRes.data.jobs || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopicsForProfession = async (professionId) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/topics/by-profession/${professionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopics(res.data.topics || []);
    } catch (error) {
      console.error('Error loading topics:', error);
      setTopics([]);
    }
  };

  const startGeneration = async (materialId) => {
    if (generationMode === 'material' && !materialId) {
      toast.error('Selecciona un material primero');
      return;
    }
    if (generationMode === 'topics' && selectedTopics.length === 0) {
      toast.error('Selecciona al menos un tema');
      return;
    }
    
    setGenerating(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        ...generationOptions,
        generation_mode: generationMode
      };
      
      if (generationMode === 'material') {
        payload.material_id = materialId;
      } else {
        payload.topic_ids = selectedTopics;
        payload.profession_id = selectedProfession;
      }
      
      const response = await axios.post(`${API}/content/generate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Generación de contenido iniciada');
      setSelectedMaterial(null);
      setSelectedTopics([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error iniciando generación');
    } finally {
      setGenerating(false);
    }
  };

  const approveContent = async (contentId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/content/approve/${contentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contenido aprobado y publicado');
      loadData();
    } catch (error) {
      toast.error('Error aprobando contenido');
    }
  };

  const rejectContent = async (contentId) => {
    if (!window.confirm('¿Rechazar y eliminar este contenido?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/content/reject/${contentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contenido rechazado');
      loadData();
    } catch (error) {
      toast.error('Error rechazando contenido');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      processing: 'bg-blue-100 text-blue-700 border-blue-200',
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      failed: 'bg-rose-100 text-rose-700 border-rose-200',
      approved: 'bg-indigo-100 text-indigo-700 border-indigo-200'
    };
    const labels = {
      pending: 'Pendiente',
      processing: 'Procesando',
      completed: 'Completado',
      failed: 'Error',
      approved: 'Aprobado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'questions': return <Brain className="w-4 h-4 text-indigo-500" />;
      case 'flashcards': return <Layers className="w-4 h-4 text-purple-500" />;
      case 'audio': return <Volume2 className="w-4 h-4 text-emerald-500" />;
      default: return <FileText className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="content-generation-flow">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Generación de Contenido</h2>
          <p className="text-slate-500">Genera exámenes, flashcards y audios a partir de materiales</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Step 1: Select Source */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">1</div>
          <h3 className="text-lg font-bold text-slate-900">Seleccionar Fuente</h3>
        </div>
        
        {/* Mode Selection */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setGenerationMode('material')}
            className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${
              generationMode === 'material' 
                ? 'border-indigo-500 bg-indigo-50' 
                : 'border-slate-200 hover:border-indigo-300'
            }`}
          >
            <FileText className="w-6 h-6 mb-2 text-indigo-600" />
            <p className="font-medium">Desde Material</p>
            <p className="text-xs text-slate-500">Genera contenido a partir de un archivo subido</p>
          </button>
          <button
            onClick={() => setGenerationMode('topics')}
            className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${
              generationMode === 'topics' 
                ? 'border-indigo-500 bg-indigo-50' 
                : 'border-slate-200 hover:border-indigo-300'
            }`}
          >
            <BookOpen className="w-6 h-6 mb-2 text-purple-600" />
            <p className="font-medium">Por Temas</p>
            <p className="text-xs text-slate-500">Genera contenido por temas específicos de una oposición</p>
          </button>
        </div>

        {/* Material Selection */}
        {generationMode === 'material' && (
          materials.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Upload className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay materiales disponibles.</p>
              <p className="text-sm">Sube archivos desde la pestaña "Temarios" o importa desde Google Drive.</p>
            </div>
          ) : (
            <div className="grid gap-3 max-h-64 overflow-y-auto">
              {materials.slice(0, 20).map((material) => (
                <div
                  key={material.material_id}
                  onClick={() => setSelectedMaterial(material)}
                  className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedMaterial?.material_id === material.material_id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-900">{material.title || material.filename}</p>
                      <p className="text-xs text-slate-500">
                        {material.content_type || 'material'} • {new Date(material.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {selectedMaterial?.material_id === material.material_id && (
                    <Check className="w-5 h-5 text-indigo-600" />
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {/* Topics Selection */}
        {generationMode === 'topics' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Selecciona una oposición</label>
              <select
                value={selectedProfession}
                onChange={(e) => { setSelectedProfession(e.target.value); setSelectedTopics([]); }}
                className="w-full p-3 rounded-lg border border-slate-200"
              >
                <option value="">-- Seleccionar oposición --</option>
                {professions?.filter(p => p.is_active !== false).map(prof => (
                  <option key={prof.profession_id} value={prof.profession_id}>
                    {prof.name}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedProfession && topics.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Selecciona temas ({selectedTopics.length} seleccionados)
                </label>
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-3 space-y-2">
                  {topics.map(topic => (
                    <label 
                      key={topic.topic_id} 
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedTopics.includes(topic.topic_id) ? 'bg-indigo-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTopics.includes(topic.topic_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTopics([...selectedTopics, topic.topic_id]);
                          } else {
                            setSelectedTopics(selectedTopics.filter(t => t !== topic.topic_id));
                          }
                        }}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm">{topic.name}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedTopics(topics.map(t => t.topic_id))}
                  >
                    Seleccionar todos
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedTopics([])}
                  >
                    Limpiar
                  </Button>
                </div>
              </div>
            )}
            
            {selectedProfession && topics.length === 0 && (
              <p className="text-slate-500 text-center py-4">No hay temas disponibles para esta oposición</p>
            )}
          </div>
        )}
      </div>

      {/* Step 2: Configure Generation */}
      <div className={`bg-white rounded-2xl border border-slate-200 p-6 transition-opacity ${
        (generationMode === 'material' && !selectedMaterial) || (generationMode === 'topics' && selectedTopics.length === 0)
          ? 'opacity-50' : ''
      }`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">2</div>
          <h3 className="text-lg font-bold text-slate-900">Configurar Generación</h3>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {/* Questions */}
          <div className={`p-4 rounded-xl border ${generationOptions.generate_questions ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-indigo-600" />
                <span className="font-medium">Preguntas</span>
              </div>
              <input
                type="checkbox"
                checked={generationOptions.generate_questions}
                onChange={(e) => setGenerationOptions(prev => ({ ...prev, generate_questions: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300"
              />
            </div>
            {generationOptions.generate_questions && (
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Cantidad</label>
                <Input
                  type="number"
                  min="5"
                  max="100"
                  value={generationOptions.num_questions}
                  onChange={(e) => setGenerationOptions(prev => ({ ...prev, num_questions: parseInt(e.target.value) || 20 }))}
                  className="h-9"
                />
              </div>
            )}
          </div>

          {/* Flashcards */}
          <div className={`p-4 rounded-xl border ${generationOptions.generate_flashcards ? 'border-purple-300 bg-purple-50' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-600" />
                <span className="font-medium">Flashcards</span>
              </div>
              <input
                type="checkbox"
                checked={generationOptions.generate_flashcards}
                onChange={(e) => setGenerationOptions(prev => ({ ...prev, generate_flashcards: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300"
              />
            </div>
            {generationOptions.generate_flashcards && (
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Cantidad</label>
                <Input
                  type="number"
                  min="5"
                  max="50"
                  value={generationOptions.num_flashcards}
                  onChange={(e) => setGenerationOptions(prev => ({ ...prev, num_flashcards: parseInt(e.target.value) || 15 }))}
                  className="h-9"
                />
              </div>
            )}
          </div>

          {/* Audio */}
          <div className={`p-4 rounded-xl border ${generationOptions.generate_audio ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-emerald-600" />
                <span className="font-medium">Audio Resumen</span>
              </div>
              <input
                type="checkbox"
                checked={generationOptions.generate_audio}
                onChange={(e) => setGenerationOptions(prev => ({ ...prev, generate_audio: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300"
              />
            </div>
            {generationOptions.generate_audio && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500">Tipo de audio</label>
                  <select
                    value={generationOptions.audio_type}
                    onChange={(e) => setGenerationOptions(prev => ({ ...prev, audio_type: e.target.value }))}
                    className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
                  >
                    <option value="summary">Resumen completo</option>
                    <option value="concepts">Conceptos clave</option>
                    <option value="quiz">Preguntas de repaso</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Número de audios</label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={generationOptions.num_audios}
                    onChange={(e) => setGenerationOptions(prev => ({ ...prev, num_audios: parseInt(e.target.value) || 1 }))}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Duración aprox. (minutos)</label>
                  <Input
                    type="number"
                    min="1"
                    max="15"
                    value={generationOptions.audio_duration_minutes}
                    onChange={(e) => setGenerationOptions(prev => ({ ...prev, audio_duration_minutes: parseInt(e.target.value) || 3 }))}
                    className="h-9"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={() => startGeneration(selectedMaterial?.material_id)}
            disabled={
              (generationMode === 'material' && !selectedMaterial) || 
              (generationMode === 'topics' && selectedTopics.length === 0) || 
              generating || 
              (!generationOptions.generate_questions && !generationOptions.generate_flashcards && !generationOptions.generate_audio)
            }
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Iniciar Generación
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Step 3: Review & Approve */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">3</div>
          <h3 className="text-lg font-bold text-slate-900">Revisar y Aprobar</h3>
          {pendingContent.length > 0 && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
              {pendingContent.length} pendientes
            </span>
          )}
        </div>

        {/* Generation Jobs */}
        {generationJobs.filter(j => j.status !== 'completed').length > 0 && (
          <div className="mb-6 space-y-3">
            <p className="text-sm font-medium text-slate-700">Trabajos en proceso</p>
            {generationJobs.filter(j => j.status !== 'completed').map((job) => (
              <div key={job.job_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Loader2 className={`w-4 h-4 ${job.status === 'processing' ? 'animate-spin text-blue-600' : 'text-slate-400'}`} />
                  <div>
                    <p className="text-sm font-medium">{job.material_title || 'Generación'}</p>
                    <p className="text-xs text-slate-500">{job.job_id}</p>
                  </div>
                </div>
                {getStatusBadge(job.status)}
              </div>
            ))}
          </div>
        )}

        {/* Pending Content */}
        {pendingContent.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay contenido pendiente de aprobación</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingContent.map((content) => (
              <div key={content.content_id} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getContentTypeIcon(content.content_type)}
                    <div>
                      <p className="font-medium text-slate-900">{content.title}</p>
                      <p className="text-xs text-slate-500">
                        {content.content_type} • {content.count} elementos • {new Date(content.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(content.status)}
                </div>

                {/* Preview */}
                {content.preview && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-3 text-sm text-slate-600 max-h-32 overflow-y-auto">
                    {content.preview}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewContent(content)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Vista previa
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => approveContent(content.content_id)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Aprobar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => rejectContent(content.content_id)}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{previewContent.title}</h3>
                <p className="text-sm text-slate-500">
                  {previewContent.content_type} • {previewContent.count} elementos
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPreviewContent(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Full preview content */}
              {previewContent.preview && (
                <div className="mb-4">
                  <h4 className="font-medium text-slate-700 mb-2">Vista previa:</h4>
                  <div className="bg-slate-50 rounded-lg p-4 text-sm whitespace-pre-wrap">
                    {previewContent.preview}
                  </div>
                </div>
              )}
              
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Estado:</span>
                  <span className="ml-2 font-medium">{previewContent.status}</span>
                </div>
                <div>
                  <span className="text-slate-500">Creado:</span>
                  <span className="ml-2">{new Date(previewContent.created_at).toLocaleString()}</span>
                </div>
                {previewContent.topic_name && (
                  <div>
                    <span className="text-slate-500">Tema:</span>
                    <span className="ml-2">{previewContent.topic_name}</span>
                  </div>
                )}
                {previewContent.source && (
                  <div>
                    <span className="text-slate-500">Fuente:</span>
                    <span className="ml-2">{previewContent.source}</span>
                  </div>
                )}
              </div>
              
              {/* Items list if available */}
              {previewContent.items && previewContent.items.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-slate-700 mb-2">Elementos ({previewContent.items.length}):</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {previewContent.items.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="bg-slate-50 rounded p-3 text-sm">
                        {item.question_text || item.front || item.title || JSON.stringify(item).slice(0, 200)}
                      </div>
                    ))}
                    {previewContent.items.length > 10 && (
                      <p className="text-slate-500 text-sm">
                        ...y {previewContent.items.length - 10} elementos más
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPreviewContent(null)}>
                Cerrar
              </Button>
              <Button 
                onClick={() => {
                  approveContent(previewContent.content_id);
                  setPreviewContent(null);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Check className="w-4 h-4 mr-1" />
                Aprobar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentGenerationFlow;
